<template>
  <div>
    <PageHeader :breadcrumRoutes="routes_pageheader" title="Empleados" />

    <a-card>
      <a-row align="middle" justify="space-between">
        <a-typography-title :level="4" class="text-gray-8 mb-0">
          Lista de empleados
        </a-typography-title>

        <a-button type="primary" @click="showDownloadStockModal">
          Descargar
        </a-button>
        <StockDownloadModal
          v-model:visible="downloadStockModal"
          :policies="companyPolicies"
          :loading="loadingPolicies"
          :filters-enabled="Boolean(selectedCompany)"
          :blocked-reason="selectedCompany ? '' : 'Selecciona una empresa'"
          :reset-key="selectedCompany"
          @download="handleDownloadStock"
        >
          <template #company>
            <MainFilter :multipleCompanies="false" />
          </template>
        </StockDownloadModal>
      </a-row>

      <EmployeesTable class="mt-3" />
    </a-card>
  </div>
</template>

<script setup>
import PageHeader from '@/components/page-header.vue';
import EmployeesTable from '@/components/rrhh/employees-table.vue';
import MainFilter from '@/components/dashboard/select-filter.vue';
import { SELECTED_COMPANY, DOWNLOAD_EXCEL_STOCK } from '@/store/types';
import {
  FETCH_COMPANY_POLICIES,
  GET_COMPANY_POLICIES,
  SET_COMPANY_POLICIES
} from '@/store/clients/policies/types';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { ref, computed, watch } from 'vue';
import notification from '@/utils/notifications';
import { validateToken } from '@/network/interceptors/validateToken';
import StockDownloadModal from '@/components/clients/stock/stock-download-modal.vue';

const store = useStore();
const router = useRouter();

const routes_pageheader = [
  {
    breadcrumbName: 'Empleados'
  }
];

const downloadStockModal = ref(false);
const showDownloadStockModal = () => {
  downloadStockModal.value = true;
};

const selectedCompany = computed(() => {
  const companyId = store.getters[SELECTED_COMPANY][0];
  return Array.isArray(companyId) ? undefined : companyId;
});

const companyPolicies = computed(() => store.getters[GET_COMPANY_POLICIES]);
const loadingPolicies = ref(false);

// Each company switch starts a new request; only the latest one may leave its
// policies in the store, so a slow response can't show another company's.
let policiesRequestId = 0;
let latestPolicies = null;

watch(
  selectedCompany,
  async (companyId) => {
    policiesRequestId += 1;
    const requestId = policiesRequestId;
    latestPolicies = null;
    store.commit(SET_COMPANY_POLICIES, []);
    loadingPolicies.value = Boolean(companyId);
    if (!companyId) return;

    try {
      const resp = await store.dispatch(
        FETCH_COMPANY_POLICIES,
        `?companyId=${companyId}`
      );
      if (requestId === policiesRequestId) latestPolicies = resp.data;
    } catch (error) {
      if (requestId === policiesRequestId) {
        notification(
          'error',
          'Error',
          'No se pudieron cargar las pólizas de la empresa.'
        );
      }
    } finally {
      if (requestId === policiesRequestId) {
        loadingPolicies.value = false;
      } else {
        // The action already stored this outdated list: restore the current one.
        store.commit(SET_COMPANY_POLICIES, latestPolicies || []);
      }
    }
  },
  { immediate: true }
);

const handleDownloadStock = async ({ policyIds, activeDate } = {}) => {
  if (!selectedCompany.value) {
    notification('error', 'Error', 'Debe seleccionar una compañía.');
    return;
  }

  const isValid = await validateToken();
  if (!isValid) {
    router.push({ name: 'Login' });
    return;
  }

  const url = await store.dispatch(DOWNLOAD_EXCEL_STOCK, {
    companyId: selectedCompany.value,
    policyIds,
    activeDate
  });

  const link = document.createElement('a');
  link.href = url;
  link.click();
};
</script>
